import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PURGE } from 'redux-persist';
import { User, ValidationMethod, VerificationStatus } from 'entities/user/model/types';
import { RootState } from 'shared/config/store/store';
import { AccStatus } from 'entities/account/model/types';
import { EMI } from 'shared/model/types';
import { EventLevelType } from 'entities/notification/model/types';

type UserReducerT = {
  token: string;
  meta: User;
  // referralCode: string,
  secured: boolean;
};

const initialState: UserReducerT = {
  token: '',
  meta: {
    id: 0,
    firstName: '',
    lastName: '',
    language: 'EN',
    phone: '',
    phoneCountry: 0,
    email: '',
    emiStatuses: {} as Record<EMI, AccStatus>,
    verified: false,
    phoneVerified: false,
    status: '',
    avatarId: 0,
    migration: null,
    twoFactor: false,
    twoFactorNotification: null,
    biometrical: false,
    biometricalNotification: null,
    twoFactorSecret: null,
    hmacSecret: false,
    hmacNotification: false,
    restoreId: '',
    business: false,
    biometryKey: false,
    biometryForLogin: false,
    validateLogin: ValidationMethod.No,
    validateConfirmation: ValidationMethod.SMS,
    dateOfBirth: '',
    address: {
      line1: '',
      line2: '',
      postalCode: '',
      city: '',
      country: 0,
    },
    agree: null,
    agreeOn: null,
    // cardholderIds: [],
    incomingPaymentMessage: null,
    hasPrimaryToken: false,
    verificationStatus: VerificationStatus.Init,
    notificationLevel: EventLevelType.All,
  },
  // referralCode: '',
  secured: false,
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUser(
      state,
      action: PayloadAction<
        Partial<{
          token: string;
          secured: boolean;
          meta: Partial<User>;
        }>
      >,
    ) {
      const { token, secured, meta } = action.payload;

      if (token) state.token = token;
      if (secured !== undefined) state.secured = secured;
      if (meta) state.meta = { ...state.meta, ...meta };
    },
    setUserMeta(state, action: PayloadAction<Partial<UserReducerT['meta']>>) {
      if (action.payload) state.meta = { ...state.meta, ...action.payload };
    },
    setUserSecure(state, action: PayloadAction<boolean>) {
      state.secured = action.payload;
    },
    setValidateLogin(state, action: PayloadAction<ValidationMethod>) {
      state.meta.validateLogin = action.payload;
    },
    setValidateConfirmation(state, action: PayloadAction<ValidationMethod>) {
      state.meta.validateConfirmation = action.payload;
    },
    setBiometryForLogin(state, action: PayloadAction<boolean>) {
      state.meta.biometryForLogin = action.payload;
    },
    setBiometryKey(state, action: PayloadAction<boolean>) {
      state.meta.biometryKey = action.payload;
    },
    logout: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, () => initialState);
  },
});

export const {
  setUser,
  setUserMeta,
  setUserSecure,
  logout,
  setValidateConfirmation,
  setValidateLogin,
  setBiometryForLogin,
  setBiometryKey,
} = userSlice.actions;

export default userSlice.reducer;

const selectUserState = (state: RootState) => state.user;

export const getToken = createSelector([selectUserState], (user) => user.token);
export const getUserMeta = createSelector([selectUserState], (user) => user.meta);
export const getUserId = createSelector([selectUserState], (user) => user.meta.id);
export const getUserName = createSelector(
  [selectUserState],
  (user) => `${user.meta.firstName ?? '***'} ${user.meta.lastName ?? '***'}`,
);
export const getUserLanguage = createSelector(
  [selectUserState],
  (user) => user.meta.language ?? 'EN',
);
export const getUserSecureState = createSelector(
  [selectUserState],
  (user) => user.secured ?? false,
);

export const getUserEmiStatuses = createSelector(
  [selectUserState],
  (user) => user.meta.emiStatuses,
);

export const getUserActiveEmis = createSelector([selectUserState], (user) =>
  Object.entries(user.meta.emiStatuses)
    .filter(([, status]) => status === AccStatus.Active)
    .map(([emi]) => emi),
);

export const getValidateLogin = createSelector(
  [selectUserState],
  (user) => user.meta.validateLogin,
);

export const getValidateConfirmation = createSelector(
  [selectUserState],
  (user) => user.meta.validateConfirmation,
);

export const getBiometryForLogin = createSelector(
  [selectUserState],
  (user) => user.meta.biometryForLogin,
);

export const getBiometryKey = createSelector(
  [selectUserState],
  (user) => user.meta.biometryKey,
);
